import {
  Box,
  BoxProps,
  CloseButton,
  Divider,
  Heading,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useTooltip } from './TooltipContext';

interface InfoIconProps {
  onDarkBackground?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}

const InfoIcon: React.FC<InfoIconProps> = ({ onDarkBackground = false, onClick }) => (
  <Box
    as="button"
    onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      setTimeout(() => {
        if (onClick) onClick(e);
      }, 0);
    }}
    onMouseDown={(e: { stopPropagation: () => void; preventDefault: () => void }) => {
      e.stopPropagation();
      e.preventDefault();
    }}
    cursor="pointer"
    lineHeight="1"
    _hover={{ transform: 'scale(1.1)' }}
    transition="transform 0.2s"
    height="32px"
    width="32px"
    display="flex"
    alignItems="center"
    justifyContent="center"
    background="transparent"
    border="none"
    position="relative"
    zIndex="10"
  >
    <svg width="32" height="32" viewBox="0 0 32 45" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M31 22.5C31 29.8655 24.3792 36 16 36C7.62083 36 1 29.8655 1 22.5C1 15.1345 7.62083 9 16 9C24.3792 9 31 15.1345 31 22.5Z"
        fill={onDarkBackground ? '#000000' : 'var(--chakra-colors-primary-500)'}
        stroke="#FBFEFF"
        strokeWidth="2"
      />
      <path
        d="M14.2891 31V18.4375H17.7109V31H14.2891ZM16 17.0547C14.9688 17.0547 14.1719 16.2695 14.1719 15.2852C14.1719 14.2891 14.9688 13.5156 16 13.5156C17.0312 13.5156 17.8281 14.2891 17.8281 15.2852C17.8281 16.2695 17.0312 17.0547 16 17.0547Z"
        fill="#FCFEFF"
      />
    </svg>
  </Box>
);

interface CustomTooltipProps {
  id: string;
  title?: string;
  content?: React.ReactNode;
  onDarkBackground?: boolean;
  containerProps?: BoxProps;
  maxHeight?: string | number;
  isRichContent?: boolean;
  minWidth?: string | number;
  maxWidth?: string | number;
  placement?: 'right' | 'left' | 'top' | 'bottom';
  offset?: number;
  zIndex?: number;
}

export const TooltipModal: React.FC<CustomTooltipProps> = ({
  id,
  title = 'Information',
  content = 'Tooltip content goes here.',
  onDarkBackground = false,
  containerProps = {},
  maxHeight = '80vh',
  isRichContent = false,
  minWidth = '300px',
  maxWidth = '768px',
  placement = 'right',
  offset = 60,
}) => {
  const { activeTooltipId, setActiveTooltipId } = useTooltip();
  const [isPositioned, setIsPositioned] = useState(false);
  const [initialRender, setInitialRender] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const contentHeightRef = useRef<number>(0);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    right: number | string | undefined;
    transform: string;
  }>({ top: 0, left: 0, right: undefined as number | string | undefined, transform: '' });

  const isOpen = activeTooltipId === id;

  const checkNeedsResponsiveMode = (
    iconRect: DOMRect,
    tooltipWidth: number,
    currentPlacement: string,
    currentOffset: number,
  ) => {
    const windowWidth = window.innerWidth;

    if (currentPlacement === 'right') {
      return iconRect.right + currentOffset + tooltipWidth > windowWidth - 20;
    } else if (currentPlacement === 'left') {
      return iconRect.left - currentOffset - tooltipWidth < 20;
    } else {
      return iconRect.left + tooltipWidth > windowWidth - 20;
    }
  };

  useEffect(() => {
    if (isOpen && iconRef.current) {
      setIsPositioned(false);

      const iconRect = iconRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      const tooltipMinWidthValue =
        typeof minWidth === 'string'
          ? minWidth.endsWith('px')
            ? parseInt(minWidth)
            : parseInt(minWidth)
          : minWidth;

      const needsResponsiveMode = checkNeedsResponsiveMode(
        iconRect,
        tooltipMinWidthValue,
        placement,
        offset,
      );
      setIsMobileView(needsResponsiveMode);

      const tooltipMaxHeightValue =
        typeof maxHeight === 'number'
          ? maxHeight
          : maxHeight.endsWith('px')
          ? parseInt(maxHeight)
          : maxHeight.endsWith('vh')
          ? (parseInt(maxHeight) / 100) * windowHeight
          : windowHeight * 0.8;

      const tooltipHeight =
        !initialRender && contentHeightRef.current > 0
          ? Math.min(contentHeightRef.current, tooltipMaxHeightValue)
          : tooltipMaxHeightValue;

      let newPosition;

      if (needsResponsiveMode) {
        newPosition = {
          top: windowHeight / 2 - tooltipHeight / 2,
          left: 0,
          right: '10%' as number | string | undefined,
          transform: 'translateX(0)',
        };
      } else {
        let top = iconRect.top;
        let left = iconRect.right + offset;
        let right = undefined;
        let transform = '';

        if (placement === 'left') {
          left = Math.max(20, iconRect.left - offset);
        } else if (placement === 'top') {
          top = Math.max(20, iconRect.top - offset);
          left = iconRect.left;
        } else if (placement === 'bottom') {
          top = iconRect.bottom + offset;
          left = iconRect.left;
        }

        if (top + tooltipHeight + 60 > windowHeight) {
          top = Math.max(0, windowHeight - tooltipHeight - 60);
        }

        if (left + tooltipMinWidthValue > windowWidth - 20) {
          left = Math.max(20, windowWidth - tooltipMinWidthValue - 20);
        }

        newPosition = { top, left, right, transform };
      }

      setPosition(newPosition);

      setTimeout(() => {
        setIsPositioned(true);
      }, 10);
    } else {
      setIsPositioned(false);
    }
  }, [isOpen, offset, placement, initialRender, minWidth, maxWidth]);

  useEffect(() => {
    const handleDOMChange = () => {
      if (isOpen && iconRef.current) {
        setIsPositioned(false);

        const iconRect = iconRef.current.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        const tooltipMinWidthValue =
          typeof minWidth === 'string'
            ? minWidth.endsWith('px')
              ? parseInt(minWidth)
              : parseInt(minWidth)
            : minWidth;

        const needsResponsiveMode = checkNeedsResponsiveMode(
          iconRect,
          tooltipMinWidthValue,
          placement,
          offset,
        );
        setIsMobileView(needsResponsiveMode);

        const tooltipMaxHeightValue =
          typeof maxHeight === 'number'
            ? maxHeight
            : maxHeight.endsWith('px')
            ? parseInt(maxHeight)
            : maxHeight.endsWith('vh')
            ? (parseInt(maxHeight) / 100) * windowHeight
            : windowHeight * 0.8;

        const tooltipHeight =
          !initialRender && contentHeightRef.current > 0
            ? Math.min(contentHeightRef.current, tooltipMaxHeightValue)
            : tooltipMaxHeightValue;

        let newPosition;

        if (needsResponsiveMode) {
          newPosition = {
            top: windowHeight / 2 - tooltipHeight / 2,
            left: 0,
            right: '10%' as number | string | undefined,
            transform: 'translateX(0)',
          };
        } else {
          let top = iconRect.top;
          let left = iconRect.right + offset;
          let right = undefined;
          let transform = '';

          if (placement === 'left') {
            left = Math.max(20, iconRect.left - offset);
          } else if (placement === 'top') {
            top = Math.max(20, iconRect.top - offset);
            left = iconRect.left;
          } else if (placement === 'bottom') {
            top = iconRect.bottom + offset;
            left = iconRect.left;
          }

          if (top + tooltipHeight + 60 > windowHeight) {
            top = Math.max(0, windowHeight - tooltipHeight - 60);
          }

          if (left + tooltipMinWidthValue > windowWidth - 20) {
            left = Math.max(20, windowWidth - tooltipMinWidthValue - 20);
          }

          newPosition = { top, left, right, transform };
        }

        setPosition(newPosition);

        setTimeout(() => {
          setIsPositioned(true);
        }, 10);
      }
    };

    window.addEventListener('resize', handleDOMChange);
    window.addEventListener('scroll', handleDOMChange, true);

    return () => {
      window.removeEventListener('resize', handleDOMChange);
      window.removeEventListener('scroll', handleDOMChange, true);
    };
  }, [isOpen, offset, placement, initialRender, minWidth, maxWidth]);

  useEffect(() => {
    if (isOpen && tooltipRef.current && isPositioned) {
      const contentContainer = tooltipRef.current.children[1] as HTMLElement;

      if (contentContainer) {
        contentHeightRef.current = contentContainer.scrollHeight;

        if (initialRender) {
          setInitialRender(false);
        }
      }
    }
  }, [isOpen, isPositioned, initialRender]);

  const onToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setTimeout(() => {
      setActiveTooltipId(isOpen ? null : id);
    }, 0);
  };

  const onClose = () => setActiveTooltipId(null);

  return (
    <Box
      display="inline-block"
      ref={iconRef}
      {...containerProps}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      position="relative"
      zIndex="2"
    >
      <InfoIcon onDarkBackground={onDarkBackground} onClick={onToggle} />

      {isOpen && (
        <Portal>
          <Box
            ref={tooltipRef}
            position="fixed"
            top={`${position.top}px`}
            left={isMobileView ? '10%' : `${position.left}px`}
            right={isMobileView ? '10%' : undefined}
            transform={position.transform}
            zIndex={1400}
            minWidth={isMobileView ? 'auto' : minWidth}
            maxWidth={isMobileView ? 'auto' : maxWidth}
            maxHeight={maxHeight}
            bg="white"
            color="black"
            boxShadow="0 4px 24px 12px rgba(0, 0, 0, 0.18)"
            borderRadius="24px"
            overflow="hidden"
            opacity={isPositioned ? 1 : 0}
            transition="opacity 0.2s"
          >
            <VStack justifyContent="space-between" alignItems="center" pl={4} pr={2} gap={0}>
              <CloseButton
                size="md"
                alignSelf="flex-end"
                color="#BECBD1"
                mt={1}
                p={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              />
              <Heading as="h3" size="md" alignSelf="flex-start" mt={-2}>
                {title}
              </Heading>
              <Divider w="95%" color="#BECBD180" my={1} />
            </VStack>

            <Box p={4} overflowY="auto" maxHeight={`calc(${maxHeight} - 60px)`}>
              {isRichContent ? (
                content
              ) : typeof content === 'string' ? (
                <Text>{content}</Text>
              ) : (
                content
              )}
            </Box>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default TooltipModal;
